import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, X } from 'lucide-react';
import TenantMobileLayout from './TenantMobileLayout';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TenantMobileSpese = ({ onBack, getHeaders }) => {
  const [spese, setSpese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totale, setTotale] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [newSpesa, setNewSpesa] = useState({
    descrizione: '', importo: 0, categoria: 'Materie Prime',
    data: new Date().toISOString().split('T')[0], fornitore: '', metodo_pagamento: 'contanti'
  });

  const categorie = ["Materie Prime", "Bevande", "Utenze", "Personale", "Manutenzione", "Marketing", "Altro"];

  const loadSpese = useCallback(async () => {
    try {
      let url = `${API}/gestione/tenant/spese?limit=200`;
      if (filterCategoria !== 'all') url += `&categoria=${filterCategoria}`;
      const response = await axios.get(url, { headers: getHeaders ? getHeaders() : {} });
      setSpese(response.data.spese || []);
      setTotale(response.data.totale_importo || 0);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [filterCategoria, getHeaders]);

  useEffect(() => { loadSpese(); }, [loadSpese]);

  const handleAddSpesa = async () => {
    if (!newSpesa.descrizione || !newSpesa.importo) {
      toast.error('Inserisci descrizione e importo');
      return;
    }
    try {
      await axios.post(`${API}/gestione/tenant/spese`, newSpesa, { headers: getHeaders ? getHeaders() : {} });
      toast.success('Spesa registrata!');
      setShowAddModal(false);
      setNewSpesa({ descrizione: '', importo: 0, categoria: 'Materie Prime', data: new Date().toISOString().split('T')[0], fornitore: '', metodo_pagamento: 'contanti' });
      loadSpese();
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try {
      await axios.delete(`${API}/gestione/tenant/spese/${id}`, { headers: getHeaders ? getHeaders() : {} });
      toast.success('Spesa eliminata');
      loadSpese();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <TenantMobileLayout title="Gestione Spese" onBack={onBack}>
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Add Button */}
          <button 
            onClick={() => setShowAddModal(true)}
            className="w-full py-4 rounded-2xl border border-cyan-500/30 flex items-center justify-center gap-2 text-cyan-400 font-medium active:scale-98 transition-all"
            style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
          >
            <Plus className="h-5 w-5" />
            Nuova Spesa
          </button>

          {/* Filter */}
          <select 
            value={filterCategoria} 
            onChange={(e) => setFilterCategoria(e.target.value)}
            className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">Tutte le categorie</option>
            {categorie.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Total Card */}
          <div 
            className="rounded-2xl p-5 border border-red-500/30"
            style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.2) 0%, rgba(0,0,0,0.3) 100%)' }}
          >
            <p className="text-sm text-red-300">Totale Spese</p>
            <p className="text-3xl font-bold text-white">€{totale.toFixed(2)}</p>
          </div>

          {/* Spese List */}
          {spese.length === 0 ? (
            <div 
              className="text-center py-12 rounded-2xl border border-cyan-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
            >
              <p className="text-gray-400">Nessuna spesa registrata</p>
            </div>
          ) : (
            <div className="space-y-3">
              {spese.map(spesa => (
                <div 
                  key={spesa.id} 
                  className="rounded-2xl p-4 border border-cyan-500/20 flex items-center justify-between"
                  style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{spesa.descrizione}</h3>
                    <p className="text-xs text-cyan-400">{spesa.categoria}</p>
                    <p className="text-sm text-gray-400">{spesa.data}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-bold text-red-400">€{spesa.importo?.toFixed(2)}</p>
                    <button 
                      onClick={() => handleDelete(spesa.id)} 
                      className="w-9 h-9 rounded-lg bg-red-500/20 flex items-center justify-center"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end justify-center">
          <div 
            className="w-full max-w-md rounded-t-3xl border-t border-cyan-500/30 max-h-[90vh] overflow-y-auto"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-6 border-b border-cyan-500/20 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Nuova Spesa</h2>
              <button onClick={() => setShowAddModal(false)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Descrizione</label>
                <input
                  type="text"
                  value={newSpesa.descrizione}
                  onChange={(e) => setNewSpesa({...newSpesa, descrizione: e.target.value})}
                  className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Es: Acquisto verdure"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Importo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newSpesa.importo}
                    onChange={(e) => setNewSpesa({...newSpesa, importo: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Data</label>
                  <input
                    type="date"
                    value={newSpesa.data}
                    onChange={(e) => setNewSpesa({...newSpesa, data: e.target.value})}
                    className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Categoria</label>
                <select
                  value={newSpesa.categoria}
                  onChange={(e) => setNewSpesa({...newSpesa, categoria: e.target.value})}
                  className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Fornitore</label>
                <input
                  type="text"
                  value={newSpesa.fornitore}
                  onChange={(e) => setNewSpesa({...newSpesa, fornitore: e.target.value})}
                  className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Nome fornitore"
                />
              </div>
              <button
                onClick={handleAddSpesa}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-black font-semibold rounded-xl shadow-lg shadow-cyan-500/30 active:scale-98 transition-all"
              >
                Salva Spesa
              </button>
            </div>
          </div>
        </div>
      )}
    </TenantMobileLayout>
  );
};

export default TenantMobileSpese;
